import { type Control, type FieldValues } from 'react-hook-form';
import { withSuppressedBroadcast } from './withSuppressedBroadcast.ts';

/**
 * Accumula le chiamate `unregister` nella stessa microtask e le esegue
 * in un'unica chiamata `unregisterFn(names[])`.
 *
 * RHF internamente processa tutti i nomi nel loop, poi emette UNA sola
 * coppia di notifiche `_subjects.state.next(...)`. Senza batching,
 * 20 widget nascosti simultaneamente → 20 notifiche broadcast ×
 * N subscriber → cascata di re-render.
 *
 * Il WeakMap è indicizzato per `control` (unico per form), quindi
 * form multipli sulla stessa pagina non interferiscono.
 *
 * NOTA: accetta `control` e `unregisterFn` come parametri separati
 * (non `methods`) per evitare di dipendere dall'oggetto context di RHF
 * che cambia referenza ad ogni formState update.
 */
const batchMap = new WeakMap<
  object,
  {
    names: Set<string>;
    scheduled: boolean;
    unregisterFn: (name: string | string[], options?: any) => void;
  }
>();

/**
 * Versione "batched" di `unregister` per React Hook Form, da usare nei nostri
 * input al posto di `methods.unregister`. Accumula i nomi da deregistrare e li
 * passa tutti insieme a `unregisterFn` in una microtask successiva,
 * riducendo il numero di notifiche e re-render.
 */
export function batchedUnregister(
  control: Control<FieldValues>,
  unregisterFn: (name: string | string[], options?: any) => void,
  name: string | string[],
) {
  let batch = batchMap.get(control);
  if (!batch) {
    batch = { names: new Set(), scheduled: false, unregisterFn };
    batchMap.set(control, batch);
  }

  const names = Array.isArray(name) ? name : [name];
  for (const n of names) {
    batch.names.add(n);
  }

  if (!batch.scheduled) {
    batch.scheduled = true;
    queueMicrotask(() => {
      if (!batch) {
        // dovrebbe essere sempre definito, ma lo gestiamo per sonar
        console.error('Batching error: batch not found for control', control);
        return;
      }

      const toUnregister = [...batch.names];
      batch.names.clear();
      batch.scheduled = false;
      if (toUnregister.length > 0) {
        withSuppressedBroadcast(control, () => {
          batch.unregisterFn(toUnregister, { keepIsValid: true });
        });
      }
    });
  }
}
